import { useQuery } from "@tanstack/react-query";
import type { ActionMeta } from "@wms/domain";
import { Alert, Button, Descriptions, Modal, Space, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { LoadProblem } from "./LoadProblem";

interface PreviewData {
  message?: string;
  columns: { key: string; label: string; align?: "left" | "right" }[];
  rows: Record<string, string>[];
  summary?: { label: string; value: string }[];
  /** ТЗ «Понятные шаги» UX1-044: the rows carry check boxes; the chosen keys are posted under `name`. */
  select?: { name: string; default: string[] } | null;
}

interface Props {
  entity: string;
  id: string;
  action: ActionMeta;
  pending: boolean;
  /** `body` — the chosen rows of a preview with `select`; nothing for the other previews, as before. */
  onConfirm: (body?: Record<string, unknown>) => void;
  onClose: () => void;
}

/**
 * UX of R31: an action that changes stock shows what it will post before the button — for the approval of a count
 * the book, the fact, the difference, the stock now and after — instead of a bare «Утвердить? Да / Отмена».
 * UX1-044: a preview may ask which rows to take («Пересчитать оставшиеся»): the server names the default choice,
 * the user confirms it or changes it; the confirmation sends exactly the keys that are ticked.
 */
export function PreviewConfirmModal({ entity, id, action, pending, onConfirm, onClose }: Props) {
  const preview = useQuery({
    queryKey: [entity, id, "action-preview", action.preview],
    queryFn: () => api.get<PreviewData>(`/${entity}/${id}/${action.preview}`),
  });
  const select = preview.data?.select ?? null;
  const [chosen, setChosen] = useState<string[] | null>(null);
  useEffect(() => {
    if (select) setChosen(select.default);
  }, [select]);
  const selected = chosen ?? select?.default ?? [];
  const label = action.label;
  return (
    <Modal title={`${label}: проверьте изменения`} open onCancel={onClose} footer={null} width={980} destroyOnHidden>
      {preview.isError ? <LoadProblem error={preview.error} onRetry={() => preview.refetch()} /> : null}
      {preview.data?.message || action.outcome ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={preview.data?.message ?? action.outcome} data-testid="preview-message" />
      ) : null}
      {preview.data?.summary?.length ? (
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }} bordered style={{ marginBottom: 12 }}>
          {preview.data.summary.map((s) => (
            <Descriptions.Item key={s.label} label={s.label}>
              {s.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ) : null}
      <Table
        size="small"
        rowKey={(r) => r.key ?? JSON.stringify(r)}
        loading={preview.isLoading}
        pagination={false}
        scroll={{ x: "max-content", y: 420 }}
        dataSource={preview.data?.rows ?? []}
        rowClassName={(r) => (r.tone === "warning" ? "wms-row-warning" : "")}
        rowSelection={
          select
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setChosen(keys.map(String)),
                columnTitle: "Взять",
                getCheckboxProps: (r) => ({ "aria-label": `Взять строку ${r.line_no ?? r.object ?? r.key}` }) as Record<string, unknown>,
              }
            : undefined
        }
        columns={(preview.data?.columns ?? []).map((c) => ({ title: c.label, dataIndex: c.key, align: c.align ?? "left" }))}
        locale={{ emptyText: select ? "Строк нет: брать нечего" : "Строк нет: утверждение ничего не изменит" }}
      />
      <Space style={{ width: "100%", justifyContent: "space-between", marginTop: 16 }} wrap>
        <Typography.Text type="secondary" data-testid="preview-footnote">
          {select ? `Выбрано строк: ${selected.length}. Цифры взяты из строк документа и текущих остатков.` : "Цифры взяты из строк документа и текущих остатков."}
        </Typography.Text>
        <Space>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            type="primary"
            danger={action.danger}
            loading={pending}
            disabled={!preview.isSuccess || (!!select && selected.length === 0)}
            data-testid="preview-confirm"
            onClick={() => onConfirm(select ? { [select.name]: selected } : undefined)}
          >
            {label}
          </Button>
        </Space>
      </Space>
    </Modal>
  );
}
